import React from 'react'
import {
  Checkbox,
  Dropdown,
  Menu,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import {
  DownSquareOutlined,
  LinkOutlined,
  UserOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { NO_APPRAISER_LAR_REASONS } from '@app/common/constants/alerts'
import Column from '@app/common/tables/Column'
import { checkPermission, checkTableData, checkTableDataWithTime, getUTCTime, renderStatusBadge, utcToLocal } from '@app/common/utils/helpers'
import Rephrase from '@app/common/utils/Rephrase'
import FlagContainer from '@app/pages/orders/FlagContainer'
import { DAYS_TO_CLOSE_COLUMN_NAME, CLOSING_DATE_COLUMN_NAME } from '@app/store/reducers/underwriters/constants'
import analytics from '@helpers/segment'
import ConsumerAlerts from './ConsumerAlerts'
import OrderAlerts from './OrderAlerts'
import { formatAppraiserColumn } from './appraiserColumnFormatter'


const OrderTableColumns = ({
  closingDateDaysToCloseColumn,
  cu_filter,
  cu_max,
  cu_min,
  cuHandler,
  custom_los_fields,
  custom_panel,
  enableVaOrderDueAndInspectionDate,
  handleUpdateUnderwriter,
  is_amc_lender,
  lenderFilterOptions,
  onFilter,
  orderList,
  ordersUnderwriterState,
  paymentStatusFilterOptions,
  pipeline_column_order,
  pipeline_column_settings,
  productFilterOptions,
  selectedDefaultViews,
  selectedOrderIds,
  setSelectedOrderIds,
  status_filter,
  trackState,
  unapprovedMsgFilterSetting,
  underwriters,
  updateFlagCount,
}) => {
  /**
   * OrderTableColumns returns a list of components intended to be supplied as the child columns of the Table component.
   *
   * Despite being written as a functional component, this function must be called directly to work with the Table.
   */
  const ableToViewPaymentIcon = checkPermission('payments_received_view')
  let togglePaymentColumnClass = ''
  const customDateField = pipeline_column_settings?.custom_date_field

  if (ableToViewPaymentIcon) {
    togglePaymentColumnClass = 'view-payment-column'
  } else {
    togglePaymentColumnClass = 'hide-payment-column'
  }

  const cols = [
    <Column
      className="priority"
      key='priority'
      field="priority"
      format={(priority, item) => {
        return <p className={item.priority === 'Rush' ? 'rush-order-indicator' : 'd-none'}>Rush</p>
      }}
      title=""
      width="2%"
    />,
    <Column
      className="flags flags-left-padding"
      key='flags'
      field="flags"
      format={(flags, item) => <FlagContainer key={item} updateFlagCount={updateFlagCount} order={item} />}
      title=""
      width="2%"
      isDashboard={true}
    />,
    <Column
      className="order_id"
      field="number"
      format={(number, item) => {
        if (!item.is_follow_up) {
          return <Link onClick={() => {
            analytics.trackComponent('Orders Single Order Clicked', {
              order_id: item.id,
              searches_until_order_click: trackState.searchesUntilOrderClick,
              page_clicks_until_order_click: trackState.pageClicksUntilOrderClick,
            })
          }} to={`/orders/${item.id}`} id={item.id}>{number || 'View'}</Link>
        } else {
          return (
            <div className='followup-container'>
              <div className='link-icon'>
                <LinkOutlined />
              </div>
              <div className='followup-order'>
                <div className='linked-order'></div>
                {item.is_last ? <div className='linked-order no-border'></div> : <div className='linked-order'></div>}
              </div>
            </div>
          )
        }
      }}
      key='order_id'
      title={'Order ID'}
    />,
  ]

  const generateProductFilter = (product, item) => {
    const MAX_LEN_PRODUCT_NAME = 20

    const productsList = item.products.filter(p => p).map((product, productIndex) => {
      let productDisplay = <small className='text-muted'>{product.product_name}</small>
      // We have a namespace conflict between the Single order page when there are multiple orders, and
      // the order table, so if we click on a primary from the order table, then click on a follow up tab in the single
      // order page, then click on the Orders breadcrumb, the orders namespace has weird and unexpected data.
      if (product.product_name && product.product_name.length > MAX_LEN_PRODUCT_NAME) {
        const smallName = product.product_name.substring(0, MAX_LEN_PRODUCT_NAME) + '...'
        productDisplay = <Tooltip title={product.product_name}><small className='text-muted'>{smallName}</small></Tooltip>
      }
      return <div key={productIndex}>{productDisplay}</div>
    })
    return <div>{productsList}</div>
  }

  const formatSubmissionData = (submissionData) => {
    const formatedDate = checkTableData(submissionData)
    if (formatedDate) {
      return <Tooltip title="Latest Submission Date">{formatedDate}</Tooltip>
    }
    return formatedDate
  }

  const getCustomDateFieldColumnProps = customFieldName => {
    const customField = (custom_los_fields || []).find(field =>
      field.mapping_field_name === customFieldName
    )
    if (!customField) {
      return { title: 'Custom Field', format: value => value }
    }
    let title = customField.field_name
    let format = value => value
    if (title === CLOSING_DATE_COLUMN_NAME && closingDateDaysToCloseColumn) {
      title = DAYS_TO_CLOSE_COLUMN_NAME
      format = value => {
        if (!value) {
          return null
        }
        const daysToClose = moment(value).startOf('day').diff(moment().startOf('day'), 'days')
        const daysText = daysToClose === 1 ? 'day' : 'days'
        return <Tag className='days-to-close' color={daysToClose < 5 ? 'red' : 'green'}>{Math.abs(daysToClose)} {daysText}</Tag>
      }
    }
    return { title, format }
  }

  const mapPipelineColumnSettingToHtml = {
    loan_officer: (
      <Column
        className="loan_officer"
        field='loan_officer'
        format={(loanOfficer, item) => {
          if (loanOfficer) {
            return <small id={item.loan_officer.id}>{loanOfficer}</small>
          } else {
            return <small><span className="badge badge-danger">No Loan Officer</span></small>
          }
        }}
        key='loan_officer'
        title={'Loan Officer'}
      />
    ),
    address: (
      <Column
        className='address'
        field="address"
        format={(address, item) => {
          const addressItems = []
          let realAddress = ''

          if (address) {
            addressItems.push(address)
          }

          if (item.city) {
            addressItems.push(item.city)
          }

          if (item.state) {
            addressItems.push(item.state)
          }

          if (addressItems.length > 0) {
            realAddress = addressItems.join(', ')
          }

          if (item.is_follow_up && item.priority !== 'Rush') {
            return <Link to={{ pathname: `/orders/${item.primaryOrderId}`, state: { followUpOrder: item.id, loading: true } }}
              id={item.id}>{item.product_name}</Link>
          } else if (item.is_follow_up && item.priority === 'Rush') {
            return <div className="followup-order-rush-container">
              <div className="followup-order-rush">
                <p className="followup-rush-order-indicator">Rush</p>
              </div>
              <Link to={{
                pathname: `/orders/${item.primaryOrderId}`,
                state: { followUpOrder: item.id, loading: true },
              }} id={item.id}>{item.product_name}</Link>
            </div>
          }
          return (
            <div>
              <span className='text-nowrap'>{realAddress}</span>
              <br/>
              <small className='text-muted'>{item.client_name}</small>
            </div>)
        }}
        key='address'
        title={'Address'}
        width={'23%'}
      />
    ),
    product: (<Column
      className='product_filter'
      field='product_filter'
      filterable={productFilterOptions}
      format={(product, item) => {
        return (item.products && generateProductFilter(product, item))
      }}
      key='product_filter'
      onFilter={onFilter}
      title={'Products'}
    />),
    payment: (<Column
      className={togglePaymentColumnClass}
      field='consumer'
      filterable={paymentStatusFilterOptions}
      format={(consumer, item) => <ConsumerAlerts order={item} />}
      onFilter={onFilter}
      key='payment'
      title={'Payment'}
    />),
    ordered: (
      <Column
        className='ordered'
        field="created"
        format={(created, item) => checkTableData(created)}
        key='ordered'
        orderable="desc"
        title={'Ordered'}
      />
    ),
    due_date: (
      <Column
        className='due_date'
        field='lender_due_date'
        format={
          (due, item) => {
            if (item.job_type !== 'va' || enableVaOrderDueAndInspectionDate) {
              return getUTCTime(due)
            } else {
              return 'N/A'
            }
          }
        }
        orderable="desc"
        orderingField='lender_due_date'
        key='due_date'
        title={'Due Date'}
      />
    ),
    submission_date: (
      <Column
        field="submission_date"
        format={(submissionDate, item) => formatSubmissionData(submissionDate)}
        key='submission_date'
        orderable="desc"
        title='Submission Date'
      />
    ),
    alerts: (
      <Column
        className='alerts'
        field='alert'
        format={(alert, item) => (<OrderAlerts order={item} is_amc_lender={is_amc_lender}/>)}
        key='alerts'
        title={'Alerts'}
        width={'7%'}
      />
    ),
    status: (
      <Column
        className='status'
        field="status"
        filterable={status_filter}
        format={(status, item) => status === 'Inspection Scheduled' ? (
          <Tooltip title={utcToLocal(item.inspection_date).format('MMM Do YYYY, h:mm a')}>
            <small>{renderStatusBadge(status, is_amc_lender, item)}</small>
          </Tooltip>
        ) : (
          <small>{renderStatusBadge(status, is_amc_lender, item)}</small>
        )}
        key='status'
        onFilter={onFilter}
        title={'Status'}
      />
    ),
    custom_date_field: (
      <Column
        className='custom_date_field'
        field={`loan_additional_data_fields.${customDateField}`}
        key='custom_date_field'
        {...getCustomDateFieldColumnProps(customDateField)}
      />
    ),
    underwriter: (
      <Column
        className='underwriter'
        field='underwriter'
        key='underwriter'
        title='Underwriter'
        format={(underwriter, item) => {
          const underwriterFromState = ordersUnderwriterState[item.id]
          let underwriterId = underwriter
          if (underwriterFromState && underwriterFromState.id) {
            underwriterId = underwriterFromState.id
          } else if (underwriterFromState && underwriterFromState.deleted) {
            underwriterId = null
          }
          return (
            <Select
              className="underwriter-select"
              value={underwriterId}
              suffixIcon={<UserOutlined />}
              onChange={value => handleUpdateUnderwriter(value, [item.id])}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={[
                { value: null, label: <span className="unassigned-option">Unassigned</span> },
                ...(underwriters || []).map(u => ({
                  value: u.value,
                  label: <span className="assigned-option">{u.title}</span>,
                })),
              ]}
              optionRender={(option) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span>{option.label}</span>
                </div>
              )}
            />
          )
        }}
      />
    ),
    cu_scores: (
      <Column
        className='cu_score'
        cu_filter={cu_filter}
        cu_max={cu_max}
        cu_min={cu_min}
        cuHandler={cuHandler}
        field='cu_score'
        filterableRange={{ min: 1, max: 5, step: 0.1 }}
        format={(cu_score, item) => {
          if (cu_score && cu_score >= 0 && cu_score <= 5) {
            return (
              <div>
                <span key={item.id} className=''>{cu_score} out of 5<br/></span>
              </div>
            )
          } else if (cu_score) {
            return (
              <div>
                <span key={item.id} className=''>No CU Score<br/></span>
              </div>
            )
          }
          return null
        }}
        key='cu_score'
        onFilter={onFilter}
        title={'CU Scores'}
      />
    ),
    oldest_unapproved_message_date: (
      <Column
        className='oldest_unapproved_message_date'
        field="oldest_unapproved_message_date"
        format={(oldest_unapproved_message_date, item) => checkTableDataWithTime(oldest_unapproved_message_date)}
        key='oldest_unapproved_message_date'
        orderable="asc"
        title={'Unapproved Message Date'}
      />
    ),
  }

  // check the lender pipeline_column_settings and add the column to cols if it is enabled
  if (pipeline_column_settings) {
    // Create array of enabled columns
    const enabledColumns = Object.entries(pipeline_column_settings)
      .filter(([_, enabled]) => enabled)
      .map(([key]) => key)

    // Sort enabled columns based on pipeline_column_order
    const orderedColumns = enabledColumns.sort((a, b) => {
      const orderA = pipeline_column_order.indexOf(a)
      const orderB = pipeline_column_order.indexOf(b)
      return orderA - orderB
    })

    // Add columns in the sorted order
    orderedColumns.forEach(key => {
      if (key === 'loan_officer') {
        if (checkPermission('user_view_loan_officer')) {
          cols.push(mapPipelineColumnSettingToHtml[key])
        }
      } else {
        cols.push(mapPipelineColumnSettingToHtml[key])
      }
    })
  }

  if (unapprovedMsgFilterSetting && selectedDefaultViews.includes('Unapproved Messages')) {
    cols.push(mapPipelineColumnSettingToHtml.oldest_unapproved_message_date)
  }

  const hasNoAppraiserLAR = item => {
    return (item.lender_attentions || []).find(lenderAttention =>
      NO_APPRAISER_LAR_REASONS.includes(lenderAttention.reason) && !lenderAttention.resolved
    )
  }
  /*
    Handles rendering the appropriate badges in the Appraiser Column based on status & jobtype
    Use checkCanViewAppraiserNameOnOrder helper to determine if user has permission to  view the appraiser on the order
  */
  const appraiserCol = (<Column
    className='appraiser'
    key='appraiser_column'
    field="accepted"
    format={(accepted, item) => formatAppraiserColumn(accepted, item, {
      is_amc_lender,
      custom_panel,
      hasNoAppraiserLAR,
    })}
    title={<Rephrase>Appraiser</Rephrase>}
  />)

  // Add the appraiserCol based on user permission
  if (checkPermission('user_view_appraiser') || checkPermission('user_view_va_appraiser')) {
    cols.splice(4, 0, appraiserCol)
  }

  if (is_amc_lender) {
    cols.push(
      <Column
        className='lender_filter'
        field='lender_name'
        filterable={lenderFilterOptions}
        format={(lenderName, item) => (<small id={item.lender_id}>{lenderName}</small>)}
        key='lender_filter'
        onFilter={onFilter}
        title="Lender"
      />
    )
  }

  if (pipeline_column_settings?.underwriter) {
    const menu = (
      <Menu>
        <Menu.Item
          key="unassign"
          onClick={() => {
            handleUpdateUnderwriter(null, selectedOrderIds)
            setSelectedOrderIds([])
          }}
          style={{ fontWeight: 500 }}
        >
          Unassign
        </Menu.Item>
        <Menu.SubMenu
          key="reassign"
          title="Reassign"
          popupClassName="bulk-underwriter-sub-menu"
        >
          {(underwriters || []).map(u => ({ label: u.title, key: u.value })).map(
            (item) => (
              <Menu.Item
                key={item.key}
                onClick={() => {
                  handleUpdateUnderwriter(item.key, selectedOrderIds)
                  setSelectedOrderIds([])
                }}
              >
                {item.label}
              </Menu.Item>
            )
          )}
        </Menu.SubMenu>
      </Menu>
    )
    const bulkUnderwriterChangeColumn = (
      <Column
        className='bulk-underwriter-change'
        key='bulk-underwriter-change'
        hideFollowupClasses={true}
        title={(
          <div className='bulk-underwriter-controls'>
            <Checkbox
              className='bulk-underwriter-checkbox'
              checked={selectedOrderIds.length > 0}
              onChange={({ target: { checked } }) => {
                if (checked) {
                  setSelectedOrderIds(orderList.map(order => order.id))
                } else {
                  setSelectedOrderIds([])
                }
              }}
            />
            <Dropdown
              overlay={menu}
              overlayStyle={{ minWidth: 150 }}
              disabled={selectedOrderIds.length === 0}
            >
              <a onClick={e => e.preventDefault()}>
                <Space>
                  <DownSquareOutlined
                    className={
                      selectedOrderIds.length > 0
                        ? 'bulk-underwriter-dropdown-icon-active'
                        : 'bulk-underwriter-dropdown-icon-inactive'
                    }
                  />
                </Space>
              </a>
            </Dropdown>
          </div>
        )}
        format={(bulkUnderwriterChange, item) => {
          return <Checkbox
            checked={selectedOrderIds.includes(item.id)}
            onChange={({ target: { checked } }) => {
              if (checked) {
                setSelectedOrderIds([...selectedOrderIds, item.id])
              } else {
                setSelectedOrderIds(selectedOrderIds.filter(id => id !== item.id))
              }
            }}
          />
        }}
      />
    )
    cols.splice(0, 0, bulkUnderwriterChangeColumn)
  }

  return cols
}

export default OrderTableColumns
